import {
    LabelId,
    PriorityId,
    ResolutionId,
    TicketSearchDirection,
    TicketSearchOrder,
    TicketKey,
    TicketListId,
    TicketSearchRequest,
    TicketStatusId,
    TicketTypeId,
} from "@/api/tickets";
import { ProjectId, ProjectKey, ProjectVersionId } from "@/api/projects";
import { UserId } from "@/api/users";
import { useProjectsStore } from "@/store/projects";
import { defineStore } from "pinia";
import Sortable from "sortablejs";
import { Ref } from "vue";

const STORAGE_KEY = "procyon.ticketSearch";

type PersistedState = {
    request: TicketSearchRequest;
    shownFields: ShownFields;
};

function loadPersistedState(storage: Storage): PersistedState | null {
    try {
        const raw = storage.getItem(STORAGE_KEY);
        if (!raw) return null;
        return JSON.parse(raw) as PersistedState;
    } catch {
        return null;
    }
}

export type TicketSearchState = {
    request: TicketSearchRequest;
    // The last search the user actually ran. The search queries key off this
    // snapshot, so form edits fetch nothing until a trigger commits them.
    submitted: TicketSearchRequest | null;
    options: TicketSearchOptions;
    shownFields: ShownFields;
};

export function defaultSearchRequest(): TicketSearchRequest {
    return {
        text: null,
        project: null,
        assignee: null,
        assigned: null,
        reporter: null,
        priorities: null,
        excludePriorities: false,
        resolutions: null,
        resolved: null,
        affectVersions: null,
        hasAffectVersion: null,
        fixVersions: null,
        hasFixVersion: null,
        lists: null,
        hasList: null,
        order: null,
        direction: null,
        dueDate: null,
        statuses: null,
        excludeStatuses: false,
        labels: null,
        excludeLabels: false,
        types: null,
        excludeTypes: false,
    };
}

export function searchRequestToQuery(request: TicketSearchRequest): Record<string, string> {
    const query: Record<string, string> = {};

    function setString(key: string, value: string | null): void {
        if (value != null && value !== "") query[key] = value;
    }
    function setBool(key: string, value: boolean | null): void {
        if (value != null) query[key] = value ? "1" : "0";
    }
    function setList(key: string, value: readonly string[] | null): void {
        if (value != null && value.length > 0) query[key] = value.join(",");
    }
    function setEnum(key: string, value: number | null): void {
        if (value != null) query[key] = value.toString();
    }
    // Absent means include, so old saved searches and URLs keep today's behavior.
    function setOp(key: string, exclude: boolean): void {
        if (exclude) query[key] = "not";
    }

    setString("text", request.text);
    setString("project", request.project);
    setString("assignee", request.assignee);
    setBool("assigned", request.assigned);
    setString("reporter", request.reporter);
    setList("priorities", request.priorities);
    setOp("prioritiesOp", request.excludePriorities);
    setList("statuses", request.statuses);
    setOp("statusesOp", request.excludeStatuses);
    setList("types", request.types);
    setOp("typesOp", request.excludeTypes);
    setList("labels", request.labels);
    setOp("labelsOp", request.excludeLabels);
    setList("resolutions", request.resolutions);
    setBool("resolved", request.resolved);
    setList("affectVersions", request.affectVersions);
    setBool("hasAffectVersion", request.hasAffectVersion);
    setList("fixVersions", request.fixVersions);
    setBool("hasFixVersion", request.hasFixVersion);
    setList("lists", request.lists);
    setBool("hasList", request.hasList);
    if (request.dueDate != null) {
        const dueDate = new Date(request.dueDate);
        if (!Number.isNaN(dueDate.getTime())) query["dueDate"] = dueDate.toISOString();
    }
    setEnum("order", request.order);
    setEnum("direction", request.direction);

    return query;
}

export function queryToSearchRequest(query: Record<string, unknown>): TicketSearchRequest {
    const request = defaultSearchRequest();

    function str(key: string): string | null {
        const value = query[key];
        return typeof value === "string" && value !== "" ? value : null;
    }
    function bool(key: string): boolean | null {
        const value = str(key);
        if (value === "1" || value === "true") return true;
        if (value === "0" || value === "false") return false;
        return null;
    }
    function list<T extends string>(key: string): T[] | null {
        const value = str(key);
        if (value == null) return null;

        const items = value.split(",").filter((item) => item !== "") as T[];
        return items.length > 0 ? items : null;
    }
    function enumValue(key: string, max: number): number | null {
        const value = str(key);
        if (value == null) return null;

        const parsed = Number(value);
        return Number.isInteger(parsed) && parsed >= 0 && parsed <= max ? parsed : null;
    }

    request.text = str("text");
    request.project = str("project") as ProjectId | null;
    request.assignee = str("assignee") as UserId | null;
    request.assigned = bool("assigned");
    request.reporter = str("reporter") as UserId | null;
    request.priorities = list<PriorityId>("priorities");
    request.excludePriorities = str("prioritiesOp") === "not";
    request.statuses = list<TicketStatusId>("statuses");
    request.excludeStatuses = str("statusesOp") === "not";
    request.types = list<TicketTypeId>("types");
    request.excludeTypes = str("typesOp") === "not";
    request.labels = list<LabelId>("labels");
    request.excludeLabels = str("labelsOp") === "not";
    request.resolutions = list<ResolutionId>("resolutions");
    request.resolved = bool("resolved");
    request.affectVersions = list<ProjectVersionId>("affectVersions");
    request.hasAffectVersion = bool("hasAffectVersion");
    request.fixVersions = list<ProjectVersionId>("fixVersions");
    request.hasFixVersion = bool("hasFixVersion");
    request.lists = list<TicketListId>("lists");
    request.hasList = bool("hasList");

    const dueDate = str("dueDate");
    if (dueDate != null) {
        const parsed = new Date(dueDate);
        if (!Number.isNaN(parsed.getTime())) request.dueDate = parsed;
    }

    request.order = enumValue("order", TicketSearchOrder.List);
    request.direction = enumValue("direction", TicketSearchDirection.Descending);

    return request;
}

// Recognizes an exact ticket key (`<PROJECT_KEY>-<number>`) so the search box
// can jump straight to a ticket. Leading zeros are tolerated; the project key
// must match one the user can see, matched case-insensitively but returned in
// its canonical casing so it resolves against the router.
export function parseExactTicketKey(
    text: string | null,
    projectKeys: readonly ProjectKey[]
): TicketKey | null {
    if (text == null) return null;
    const match = text.trim().match(/^([A-Za-z][A-Za-z0-9]*)-0*(\d+)$/);
    if (match == null) return null;

    const keyPart = match[1]!;
    const number = match[2]!;
    const project = projectKeys.find((k) => k.toUpperCase() === keyPart.toUpperCase());
    if (project == null) return null;

    return `${project}-${number}` as TicketKey;
}

export type TicketSearchOptions = {
    page: number | null;
    perPage: number | null;
};

export type ShownFields = {
    project: boolean;
    assignees: boolean;
    reporter: boolean;
    priorities: boolean;
    resolutions: boolean;
    labels: boolean;
    statuses: boolean;
    types: boolean;
    affectsVersions: boolean;
    fixVersions: boolean;
    lists: boolean;
};

export type SortableType = Sortable;

export interface SortableEvent {
    oldIndex: number;
    newIndex: number;
}

export enum SearchComponentIndex {
    ProjectSearch = 0,
    AssigneeSearch = 1,
    ReporterSearch = 2,
    PrioritySearch = 3,
    ResolutionSearch = 4,
    LabelSearch = 5,
    StatusSearch = 6,
    TypeSearch = 7,
    AffectVersionSearch = 8,
    FixVersionSearch = 9,
    ListSearch = 10,
}

export type SearchItem<T> = {
    title: string;
    component: T;
    itemIndex: SearchComponentIndex;
    visible: Ref<boolean>;
    allowed?: Ref<boolean>;
};

export const useTicketSearchStore = defineStore("ticketSearch", {
    state: (): TicketSearchState => ({
        request: defaultSearchRequest(),
        submitted: null,
        options: {
            page: null,
            perPage: null,
        },
        shownFields: {
            project: true,
            assignees: false,
            reporter: false,
            priorities: false,
            resolutions: false,
            labels: false,
            statuses: false,
            types: false,
            affectsVersions: false,
            fixVersions: false,
            lists: false,
        },
    }),
    actions: {
        // Committing snapshots the request (the query-string round trip both
        // normalizes and deep-copies it) so later form edits leave it alone.
        commitSearch(page = 0) {
            this.$state.submitted = queryToSearchRequest(searchRequestToQuery(this.$state.request));
            this.$state.options.page = page;

            if (this.getShowProject && !this.$state.shownFields.project) this.showProject(true);
            if (this.getShowAssignees && !this.$state.shownFields.assignees)
                this.showAssignees(true);
            if (this.getShowReporter && !this.$state.shownFields.reporter) this.showReporter(true);
            if (this.getShowPriorities && !this.$state.shownFields.priorities)
                this.showPriorities(true);
            if (this.getShowResolutions && !this.$state.shownFields.resolutions)
                this.showResolutions(true);
            if (this.getShowLabels && !this.$state.shownFields.labels) this.showLabels(true);
            if (this.getShowStatuses && !this.$state.shownFields.statuses) this.showStatuses(true);
            if (this.getShowTypes && !this.$state.shownFields.types) this.showTypes(true);
            if (this.getShowAffectsVersions && !this.$state.shownFields.affectsVersions)
                this.showAffectsVersions(true);
            if (this.getShowFixVersions && !this.$state.shownFields.fixVersions)
                this.showFixVersions(true);
            if (this.getShowLists && !this.$state.shownFields.lists) this.showLists(true);

            this.saveToStorage();
        },
        showProject(intake: boolean) {
            this.$state.shownFields.project = intake;
        },
        showAssignees(intake: boolean) {
            this.$state.shownFields.assignees = intake;
        },
        showReporter(intake: boolean) {
            this.$state.shownFields.reporter = intake;
        },
        showPriorities(intake: boolean) {
            this.$state.shownFields.priorities = intake;
        },
        showResolutions(intake: boolean) {
            this.$state.shownFields.resolutions = intake;
        },
        showLabels(intake: boolean) {
            this.$state.shownFields.labels = intake;
        },
        showStatuses(intake: boolean) {
            this.$state.shownFields.statuses = intake;
        },
        showTypes(intake: boolean) {
            this.$state.shownFields.types = intake;
        },
        showAffectsVersions(intake: boolean) {
            this.$state.shownFields.affectsVersions = intake;
        },
        showFixVersions(intake: boolean) {
            this.$state.shownFields.fixVersions = intake;
        },
        showLists(intake: boolean) {
            this.$state.shownFields.lists = intake;
        },
        clearRequest() {
            this.$state.request = defaultSearchRequest();
        },
        // The user picking a different project invalidates every filter whose ids
        // belong to the old project. Programmatic replacements go through
        // applyRequest instead, so a project + fix version arriving together
        // (version chip on the home radar, saved search) survives intact.
        setProject(project: ProjectId | null) {
            if (project === this.$state.request.project) {
                return;
            }

            this.$state.request.project = project;
            this.$state.request.affectVersions = null;
            this.$state.request.hasAffectVersion = null;
            this.$state.request.fixVersions = null;
            this.$state.request.hasFixVersion = null;
            this.$state.request.priorities = null;
            this.$state.request.excludePriorities = false;
            this.$state.request.resolutions = null;
            this.$state.request.labels = null;
            this.$state.request.excludeLabels = false;
            this.$state.request.statuses = null;
            this.$state.request.excludeStatuses = false;
            this.$state.request.types = null;
            this.$state.request.excludeTypes = false;

            if (project != null) {
                return;
            }

            this.showAffectsVersions(false);
            this.showFixVersions(false);
            this.showPriorities(false);
            this.showResolutions(false);
            this.showLabels(false);
            this.showStatuses(false);
            this.showTypes(false);
        },
        applyRequest(request: TicketSearchRequest) {
            this.$state.request = request;
            this.fillDefaultProject();
        },
        // With one project in view there is nothing to choose, so the filter
        // arrives set and the project-scoped filters unlock with it.
        fillDefaultProject() {
            if (this.$state.request.project != null) return;

            const projects = useProjectsStore().getProjects();
            if (projects?.length === 1) {
                this.$state.request.project = projects[0]?.id ?? null;
            }
        },
        clearState() {
            this.clearRequest();
            this.$state.submitted = null;
            this.$state.options.page = null;
            this.showProject(true);
            this.showAssignees(false);
            this.showReporter(false);
            this.showPriorities(false);
            this.showResolutions(false);
            this.showLabels(false);
            this.showStatuses(false);
            this.showTypes(false);
            this.showAffectsVersions(false);
            this.showFixVersions(false);
            this.showLists(false);
        },
        saveToStorage() {
            const serialized = JSON.stringify({
                request: { ...this.$state.request },
                shownFields: { ...this.$state.shownFields },
            } satisfies PersistedState);
            // Tab-local (sessionStorage) keeps each tab's filter to itself; persistent
            // (localStorage) restores the last search in a fresh session.
            try {
                sessionStorage.setItem(STORAGE_KEY, serialized);
            } catch {
                // storage may not be available
            }
            try {
                localStorage.setItem(STORAGE_KEY, serialized);
            } catch {
                // storage may not be available
            }
        },
        restoreFromStorage() {
            const persisted =
                loadPersistedState(sessionStorage) ?? loadPersistedState(localStorage);
            if (persisted) {
                // Merge over the defaults so fields added after a blob was persisted
                // come back with their default instead of undefined.
                this.$state.request = { ...defaultSearchRequest(), ...persisted.request };
                this.$state.shownFields = persisted.shownFields;
            }
            this.fillDefaultProject();
        },
        applyContext(context: Partial<TicketSearchRequest>) {
            this.clearState();
            Object.assign(this.$state.request, context);
            this.fillDefaultProject();
            this.commitSearch();
        },
    },
    getters: {
        submittedQuery(state): string {
            return state.submitted
                ? new URLSearchParams(searchRequestToQuery(state.submitted)).toString()
                : "";
        },
        effectivePerPage(state): number {
            return state.options.perPage ?? 50;
        },
        getShowProject(state) {
            return state.request.project != null || state.shownFields.project;
        },
        getShowAssignees(state) {
            return (
                state.request.assigned != null ||
                state.request.assignee != null ||
                state.shownFields.assignees
            );
        },
        getShowReporter(state) {
            return state.request.reporter != null || state.shownFields.reporter;
        },
        getShowPriorities(state) {
            return state.request.priorities != null || state.shownFields.priorities;
        },
        getShowResolutions(state) {
            return (
                state.request.resolutions != null ||
                state.request.resolved != null ||
                state.shownFields.resolutions
            );
        },
        getShowLabels(state) {
            return state.request.labels != null || state.shownFields.labels;
        },
        getShowStatuses(state) {
            return state.request.statuses != null || state.shownFields.statuses;
        },
        getShowTypes(state) {
            return state.request.types != null || state.shownFields.types;
        },
        getShowAffectsVersions(state) {
            return (
                state.request.affectVersions != null ||
                state.request.hasAffectVersion != null ||
                state.shownFields.affectsVersions
            );
        },
        getShowFixVersions(state) {
            return (
                state.request.fixVersions != null ||
                state.request.hasFixVersion != null ||
                state.shownFields.fixVersions
            );
        },
        // Deliberately absent from setProject's reset: a list can belong to an
        // organization or to nobody, so a project switch does not invalidate it.
        getShowLists(state) {
            return (
                state.request.lists != null ||
                state.request.hasList != null ||
                state.shownFields.lists
            );
        },
    },
});
